import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Logo } from '../components/ui/Logo.jsx';

// Pagină publică — accesată prin link SMS de către șofer.
// Fără autentificare. Tokenul e în URL.

const API_BASE = '/api/public/sign';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
}
function formatRon(v) {
  if (v == null) return null;
  return `${Number(v).toLocaleString('ro-RO')} RON`;
}

export function PublicSign() {
  const { token } = useParams();
  const sigRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr('Token lipsă din URL.');
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/${token}`)
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw body || { error: 'fetch_failed' };
        return body;
      })
      .then((d) => setData(d))
      .catch((e) => {
        if (e?.error === 'expired') setErr('Linkul a expirat (valabil 48h).');
        else if (e?.error === 'already_signed') setErr('Contractul e deja semnat.');
        else if (e?.error === 'invalid_token') setErr('Link invalid.');
        else setErr('Eroare la încărcare: ' + (e?.error || 'necunoscută'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSign() {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setErr('Semnează în zona dedicată înainte să trimiți.');
      return;
    }
    if (!ack) {
      setErr('Confirmă acordul înainte să trimiți.');
      return;
    }
    setErr('');
    setBusy(true);
    try {
      const signature_data_url = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
      const resp = await fetch(`${API_BASE}/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_data_url, acknowledgment: true }),
      });
      const body = await resp.json().catch(() => null);
      if (!resp.ok) throw body || { error: 'submit_failed' };
      setSigned(true);
    } catch (e) {
      if (e?.error === 'already_signed') setErr('Contractul e deja semnat.');
      else if (e?.error === 'expired') setErr('Linkul a expirat.');
      else setErr('Eroare: ' + (e?.error || 'necunoscută'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <CenterFrame><div className="text-muted">Se încarcă…</div></CenterFrame>;
  }

  if (err && !data) {
    return (
      <CenterFrame>
        <div className="card text-center">
          <Logo size={48} />
          <h2 className="mt-4 text-xl font-semibold text-ink">Contract indisponibil</h2>
          <p className="mt-2 text-sm text-danger">{err}</p>
          <p className="mt-4 text-xs text-muted">Dacă ai primit acest link greșit, contactează persoana care ți l-a trimis.</p>
        </div>
      </CenterFrame>
    );
  }

  if (signed) {
    return (
      <CenterFrame>
        <div className="card text-center">
          <Logo size={48} />
          <div className="mt-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-ok/10 text-ok text-2xl">✓</div>
          <h2 className="mt-2 text-xl font-semibold text-ink">Contract semnat</h2>
          <p className="mt-2 text-sm text-muted">
            Semnătura ta a fost înregistrată. Contractul complet e disponibil la proprietar.
          </p>
        </div>
      </CenterFrame>
    );
  }

  const { rental, car } = data || {};
  const rest =
    rental?.cost_contract != null && rental?.avans_primit != null
      ? rental.cost_contract - rental.avans_primit
      : null;

  return (
    <div className="min-h-screen bg-paper pb-8">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <Logo size={30} />
          <div className="text-xs text-muted">Semnare contract de închiriere</div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-4">
        {/* Rezumat */}
        <div className="card">
          <div className="text-base font-semibold text-ink">{rental.chirias_nume}</div>
          <div className="mt-1 text-sm text-muted">
            {car.marca} {car.model} · <span className="font-mono">{car.numar_inmatriculare}</span>
          </div>
          <div className="mt-2 font-mono text-xs text-muted">
            {formatDate(rental.data_start)} → {formatDate(rental.data_end)}
          </div>
        </div>

        {/* Contract */}
        {(rental.cost_contract != null || rental.garantie != null || rental.avans_primit != null) && (
          <div className="card">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">Contract</div>
            {rental.cost_contract != null && <Row label="Cost contract" value={formatRon(rental.cost_contract)} />}
            {rental.garantie != null && <Row label="Garanție" value={formatRon(rental.garantie)} />}
            {rental.avans_primit != null && <Row label="Avans primit" value={formatRon(rental.avans_primit)} />}
            {rest != null && rest !== 0 && (
              <div className="mt-1 border-t border-border pt-2">
                <Row label={rest > 0 ? 'De achitat la retur' : 'De restituit'} value={formatRon(Math.abs(rest))} bold />
              </div>
            )}
          </div>
        )}

        {/* Preview PDF */}
        <div className="card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">Contract (PDF)</div>
          <a
            href={`${API_BASE}/${token}/contract.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-teal/20 bg-teal/5 px-3 py-2 text-sm font-semibold text-teal-bright hover:bg-teal/10"
          >
            📄 Deschide contractul
          </a>
          <p className="mt-2 text-xs text-muted">Citește-l înainte să semnezi mai jos.</p>
        </div>

        {/* Signature pad */}
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Semnătura ta</div>
            <button type="button" onClick={() => sigRef.current?.clear()} className="text-xs text-teal-bright underline">Șterge</button>
          </div>
          <div className="rounded-lg border-2 border-dashed border-border bg-white">
            <SignatureCanvas
              ref={sigRef}
              penColor="#0B2027"
              canvasProps={{ className: 'w-full h-44 rounded-lg', style: { touchAction: 'none' } }}
            />
          </div>
          <p className="mt-2 text-[11px] text-muted">
            Semnează cu degetul (telefon) sau cu mouse (desktop). Semnătura ta electronică e echivalentă cu cea olografă (art. 3 Regulament UE 910/2014).
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
          <span>Am citit și accept clauzele contractului de închiriere.</span>
        </label>

        {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

        <button
          onClick={handleSign}
          disabled={busy}
          className="btn-primary w-full h-12 text-base"
        >
          {busy ? 'Se înregistrează semnătura…' : 'Trimite semnătura'}
        </button>

        <p className="text-center text-[11px] text-muted">
          Linkul expiră la {data?.expires_at ? new Date(data.expires_at).toLocaleString('ro-RO') : '—'}
        </p>
      </main>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-muted">{label}</span>
      <span className={`font-mono ${bold ? 'font-semibold text-ink' : 'text-ink'}`}>{value}</span>
    </div>
  );
}

function CenterFrame({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-4">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
